import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  TextInput,
  ActivityIndicator,
  Modal,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Vehicle } from '../../types';
import { supabase } from '../../api/supabase';
import theme from '../../theme';
import { AssignmentConfirmationModal } from './AssignmentConfirmationModal';
import { useAssignmentFlowStore } from '../../stores/assignmentFlowStore';

interface AssignmentData {
  assignmentId: string;
  vehicleId: string;
  driverId: string;
  driverName: string;
  secondaryDriverId?: string;
  isSharedAssignment?: boolean;
}

interface VehicleSelectorModalProps {
  visible: boolean;
  onClose: () => void;
  onSelectVehicle: (vehicle: Vehicle) => void;
  navigation?: any; // For navigating (optional)
  onAssignmentCompletionRequired?: (vehicle: Vehicle, assignmentData: AssignmentData) => void; // Callback when assignment needs completion
}

export const VehicleSelectorModal: React.FC<VehicleSelectorModalProps> = ({
  visible,
  onClose,
  onSelectVehicle,
  navigation,
  onAssignmentCompletionRequired,
}) => {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [filteredVehicles, setFilteredVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'available' | 'work'>('all');
  
  // State for assignment confirmation modal
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationData, setConfirmationData] = useState<{
    vehicle: Vehicle;
    assignmentData: AssignmentData;
  } | null>(null);

  // Assignment flow store for cross-section navigation
  const setPendingAssignment = useAssignmentFlowStore((state) => state.setPendingAssignment);

  useEffect(() => {
    if (visible) {
      loadVehicles();
    }
  }, [visible]);

  useEffect(() => {
    filterVehicles();
  }, [vehicles, searchQuery, statusFilter]);

  const checkVehicleAssignment = async (vehicleId: string): Promise<AssignmentData | null> => {
    try {
      console.log('🔍 Checking for active assignment on vehicle:', vehicleId);
      
      // Query for active assignment
      const { data: assignments, error } = await supabase
        .from('assignment_history')
        .select(`
          id,
          vehicle_id,
          primary_driver_id,
          secondary_driver_id,
          is_shared,
          status,
          drivers:primary_driver_id (
            id,
            name
          )
        `)
        .eq('vehicle_id', vehicleId)
        .eq('status', 'active')
        .limit(1);
      
      if (error) {
        console.error('❌ Error checking assignment:', error);
        return null;
      }
      
      if (!assignments || assignments.length === 0) {
        console.log('✅ No active assignment found');
        return null;
      }
      
      const assignment = assignments[0];
      const driverData = assignment.drivers as any;
      const driverName = driverData?.name || 'Unknown Driver';
      
      console.log('⚠️ Active assignment found:', {
        assignmentId: assignment.id,
        driverId: assignment.primary_driver_id,
        driverName,
      });
      
      return {
        assignmentId: assignment.id,
        vehicleId: assignment.vehicle_id,
        driverId: assignment.primary_driver_id,
        driverName,
        secondaryDriverId: assignment.secondary_driver_id,
        isSharedAssignment: assignment.is_shared,
      };
    } catch (error) {
      console.error('❌ Error in checkVehicleAssignment:', error);
      return null;
    }
  };

  const loadVehicles = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('vehicles')
        .select('*')
        .in('car_status', ['available', 'work']) // Only available and work status
        .order('license_plate');

      if (error) {
        console.error('Error loading vehicles:', error);
        return;
      }

      // Map database fields to camelCase
      const mappedVehicles = (data || []).map(v => ({
        ...v,
        licensePlate: v.license_plate,
        vehicleCode: v.vehicle_code,
        makeModel: v.make_model,
        carStatus: v.car_status,
        currentDriverId: v.current_driver_id,
        dailyRent: v.daily_rental_rate,
        currentMileage: v.odometer_reading,
      }));

      setVehicles(mappedVehicles as Vehicle[]);
    } catch (error) {
      console.error('Error loading vehicles:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterVehicles = () => {
    let filtered = vehicles;

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(v => v.carStatus === statusFilter);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(v => 
        (v.licensePlate?.toLowerCase().includes(query)) ||
        (v.makeModel?.toLowerCase().includes(query)) ||
        (v.vin?.toLowerCase().includes(query))
      );
    }

    setFilteredVehicles(filtered);
  };

  const formatVehiclePlate = (vehicle: Vehicle): string => {
    const plate = vehicle.licensePlate || 'N/A';
    return plate;
  };

  const handleVehiclePress = async (vehicle: Vehicle) => {
    console.log('🚗 Vehicle pressed:', vehicle.licensePlate, 'Status:', vehicle.carStatus);
    
    // If vehicle status is 'work', check for active assignment
    // Works on both web and mobile
    if (vehicle.carStatus === 'work') {
      const assignmentData = await checkVehicleAssignment(vehicle.id);
      
      if (assignmentData) {
        // Show custom confirmation modal
        console.log('⚠️ Assignment found - showing confirmation modal');
        setConfirmationData({ vehicle, assignmentData });
        setShowConfirmation(true);
        return;
      }
    }
    
    // No active assignment or vehicle is available - proceed normally
    console.log('✅ No assignment found or vehicle available - selecting vehicle');
    onSelectVehicle(vehicle);
  };

  const handleConfirmationCancel = () => {
    console.log('❌ User cancelled - going back');
    setShowConfirmation(false);
    setConfirmationData(null);
    onClose();
  };

  const handleConfirmationComplete = () => {
    console.log('✅ User chose to complete assignment');
    if (!confirmationData) return;

    const { vehicle, assignmentData } = confirmationData;
    
    // Close confirmation modal
    setShowConfirmation(false);
    setConfirmationData(null);
    
    // Close vehicle selector modal
    onClose();
    
    // Store assignment data in Zustand store for cross-section navigation
    console.log('� Storing assignment data in flow store');
    setPendingAssignment(vehicle, assignmentData);
    
    // Navigate to Assignments section
    if (Platform.OS === 'web') {
      console.log('🌐 Web: Navigating to /assignments');
      window.location.href = '/assignments';
    } else if (navigation) {
      console.log('📱 Mobile: Navigating to Assignments');
      navigation.navigate('Assignments');
    } else {
      console.error('❌ No navigation method available');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available':
        return '#4CAF50';
      case 'work':
        return '#FFA726';
      default:
        return '#757575';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'available':
        return 'Available';
      case 'work':
        return 'Assigned'; // work = assigned to driver, not in workshop
      default:
        return status;
    }
  };

  const renderVehicleCard = ({ item }: { item: Vehicle }) => (
    <TouchableOpacity
      style={styles.vehicleCard}
      onPress={() => handleVehiclePress(item)}
    >
      <View style={styles.vehicleHeader}>
        <View style={styles.plateContainer}>
          <Ionicons name="car-sport" size={24} color={theme.colors.primary} />
          <Text style={styles.plateText}>{formatVehiclePlate(item)}</Text>
        </View>
        <View style={[styles.statusBadge, { backgroundColor: getStatusColor(item.carStatus || 'available') }]}>
          <Text style={styles.statusText}>{getStatusLabel(item.carStatus || 'available')}</Text>
        </View>
      </View>
      
      <View style={styles.vehicleDetails}>
        <View style={styles.detailRow}>
          <Ionicons name="build" size={16} color={theme.colors.textSecondary} />
          <Text style={styles.detailText}>{item.makeModel || 'N/A'}</Text>
        </View>
        {item.vin && (
          <View style={styles.detailRow}>
            <Ionicons name="qr-code" size={16} color={theme.colors.textSecondary} />
            <Text style={styles.detailText}>VIN: {item.vin}</Text>
          </View>
        )}
      </View>
    </TouchableOpacity>
  );

  return (
    <Modal
      visible={visible}
      animationType="slide"
      transparent={false}
      onRequestClose={onClose}
    >
      <View style={styles.container}>
        {/* Header */}
        <View style={styles.header}>
          <TouchableOpacity onPress={onClose} style={styles.closeButton}>
            <Ionicons name="close" size={28} color={theme.colors.textPrimary} />
          </TouchableOpacity>
          <Text style={styles.title}>Select Vehicle</Text>
          <View style={styles.placeholder} />
        </View>

        {/* Search Bar */}
        <View style={styles.searchContainer}>
          <Ionicons name="search" size={20} color={theme.colors.textSecondary} style={styles.searchIcon} />
          <TextInput
            style={styles.searchInput}
            placeholder="Search by plate, make/model, or VIN..."
            value={searchQuery}
            onChangeText={setSearchQuery}
            placeholderTextColor={theme.colors.textSecondary}
          />
          {searchQuery.length > 0 && (
            <TouchableOpacity onPress={() => setSearchQuery('')}>
              <Ionicons name="close-circle" size={20} color={theme.colors.textSecondary} />
            </TouchableOpacity>
          )}
        </View>

        {/* Status Filter */}
        <View style={styles.filterContainer}>
          <TouchableOpacity
            style={[styles.filterButton, statusFilter === 'all' && styles.filterButtonActive]}
            onPress={() => setStatusFilter('all')}
          >
            <Text style={[styles.filterText, statusFilter === 'all' && styles.filterTextActive]}>
              All ({vehicles.length})
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.filterButton, statusFilter === 'available' && styles.filterButtonActive]}
            onPress={() => setStatusFilter('available')}
          >
            <Text style={[styles.filterText, statusFilter === 'available' && styles.filterTextActive]}>
              Available ({vehicles.filter(v => v.carStatus === 'available').length})
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.filterButton, statusFilter === 'work' && styles.filterButtonActive]}
            onPress={() => setStatusFilter('work')}
          >
            <Text style={[styles.filterText, statusFilter === 'work' && styles.filterTextActive]}>
              Assigned ({vehicles.filter(v => v.carStatus === 'work').length})
            </Text>
          </TouchableOpacity>
        </View>

        {/* Vehicle List */}
        {loading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={theme.colors.primary} />
            <Text style={styles.loadingText}>Loading vehicles...</Text>
          </View>
        ) : filteredVehicles.length === 0 ? (
          <View style={styles.emptyContainer}>
            <Ionicons name="car-sport-outline" size={64} color={theme.colors.textSecondary} />
            <Text style={styles.emptyText}>No vehicles found</Text>
            <Text style={styles.emptySubtext}>
              {searchQuery.trim() ? 'Try adjusting your search' : 'No vehicles with available or work status'}
            </Text>
          </View>
        ) : (
          <FlatList
            data={filteredVehicles}
            renderItem={renderVehicleCard}
            keyExtractor={(item) => item.id}
            contentContainerStyle={styles.listContainer}
            showsVerticalScrollIndicator={false}
          />
        )}
      </View>

      {/* Assignment Confirmation Modal */}
      <AssignmentConfirmationModal
        visible={showConfirmation}
        vehicle={confirmationData?.vehicle || null}
        driverName={confirmationData?.assignmentData.driverName || ''}
        onCancel={handleConfirmationCancel}
        onComplete={handleConfirmationComplete}
      />
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing.lg,
    backgroundColor: theme.colors.white,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray200,
  },
  closeButton: {
    padding: theme.spacing.xs,
  },
  title: {
    fontSize: theme.typography.sizes.xl,
    fontWeight: theme.typography.weights.bold as any,
    color: theme.colors.textPrimary,
  },
  placeholder: {
    width: 40,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.white,
    margin: theme.spacing.md,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.gray300,
  },
  searchIcon: {
    marginRight: theme.spacing.sm,
  },
  searchInput: {
    flex: 1,
    fontSize: theme.typography.sizes.md,
    color: theme.colors.textPrimary,
  },
  filterContainer: {
    flexDirection: 'row',
    paddingHorizontal: theme.spacing.md,
    paddingBottom: theme.spacing.md,
    gap: theme.spacing.sm,
  },
  filterButton: {
    flex: 1,
    paddingVertical: theme.spacing.sm,
    paddingHorizontal: theme.spacing.md,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.gray300,
    backgroundColor: theme.colors.white,
    alignItems: 'center',
  },
  filterButtonActive: {
    backgroundColor: theme.colors.primary,
    borderColor: theme.colors.primary,
  },
  filterText: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    fontWeight: theme.typography.weights.bold as any,
  },
  filterTextActive: {
    color: theme.colors.white,
  },
  listContainer: {
    padding: theme.spacing.md,
  },
  vehicleCard: {
    backgroundColor: theme.colors.white,
    borderRadius: 12,
    padding: theme.spacing.md,
    marginBottom: theme.spacing.md,
    borderWidth: 1,
    borderColor: theme.colors.gray200,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  vehicleHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing.sm,
  },
  plateContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.sm,
  },
  plateText: {
    fontSize: theme.typography.sizes.lg,
    fontWeight: theme.typography.weights.bold as any,
    color: theme.colors.textPrimary,
  },
  statusBadge: {
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.xs,
    borderRadius: 12,
  },
  statusText: {
    fontSize: theme.typography.sizes.xs,
    fontWeight: theme.typography.weights.bold as any,
    color: theme.colors.white,
  },
  vehicleDetails: {
    gap: theme.spacing.xs,
  },
  detailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.sm,
  },
  detailText: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing.md,
  },
  loadingText: {
    fontSize: theme.typography.sizes.md,
    color: theme.colors.textSecondary,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing.xl,
  },
  emptyText: {
    fontSize: theme.typography.sizes.lg,
    fontWeight: theme.typography.weights.bold as any,
    color: theme.colors.textSecondary,
    marginTop: theme.spacing.md,
  },
  emptySubtext: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    marginTop: theme.spacing.xs,
    textAlign: 'center',
  },
});
